import React, { useEffect, useState, useCallback, useRef } from 'react';

// Horizontal tile row with hover edge-arrows and a hidden scrollbar — the
// standard streaming-app row pattern. Extracted from SearchResultsPage so the
// Mixpedia home rows share the exact same behavior/styling.
// Arrows only when there's a meaningful amount left to scroll. At the old 4px
// threshold a row overflowing by ~18px (tiles that nearly fit) still showed a
// right arrow that nudged them and stopped — which reads as "the arrow is
// broken", not "this is the end".
const EDGE = 48;

const TileScroller = ({ children, className = '' }) => {
    const ref = useRef(null);
    const [canLeft, setCanLeft] = useState(false);
    const [canRight, setCanRight] = useState(false);

    const update = useCallback(() => {
        const el = ref.current;
        if (!el) return;
        setCanLeft(el.scrollLeft > EDGE);
        setCanRight(el.scrollLeft + el.clientWidth < el.scrollWidth - EDGE);
    }, []);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        update();
        el.addEventListener('scroll', update, { passive: true });
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => {
            el.removeEventListener('scroll', update);
            ro.disconnect();
        };
    }, [update]);

    // Swapping the CONTENT (5 autocomplete mixes → 2 event mixes, a re-rolled
    // home row) changes scrollWidth without resizing the element, so neither
    // listener above fires and the arrows keep the previous row's answer — a
    // right arrow on a row with nothing to its right. `children` is a fresh
    // value each render, so this re-checks after every content change; update()
    // sets identical values when nothing moved, which React bails out of.
    useEffect(() => { update(); }, [children, update]);

    const scroll = (dir) => {
        const el = ref.current;
        if (!el) return;
        el.scrollBy({ left: dir * el.clientWidth * 0.85, behavior: 'smooth' });
    };

    return (
        <div className={`sr-scroller ${className}`}>
            {canLeft && (
                <button
                    type="button"
                    className="sr-scroller-arrow sr-scroller-arrow-left"
                    onClick={() => scroll(-1)}
                    aria-label="Scroll left"
                ><span className="sr-arrow-glyph" aria-hidden="true">‹</span></button>
            )}
            <div ref={ref} className="sr-scroller-track">{children}</div>
            {canRight && (
                <button
                    type="button"
                    className="sr-scroller-arrow sr-scroller-arrow-right"
                    onClick={() => scroll(1)}
                    aria-label="Scroll right"
                ><span className="sr-arrow-glyph" aria-hidden="true">›</span></button>
            )}
        </div>
    );
};

export default TileScroller;
